import { useMemo } from "react";
import { Session } from "@talkjs/react";
import Talk from "talkjs";
import { ErrorBoundary } from "@themes/pollinator/ui/ErrorBoundary";
import { TalkJSData } from "@core/features/chat";
import { useChatSessionRetry } from "@platform/store";

export type Props = {
  talkjsData: TalkJSData | null | undefined;
  children: React.ReactNode;
};

/**
 * Surface a structured payload when the TalkJS session fails to mount.
 *
 * The fallback is intentionally graceful (renders children without
 * <Session>) so a TalkJS outage doesn't take the rest of the app down.
 * That graceful path used to be silent — same UX whether the user has
 * no conversations or whether the SDK actually broke. This logger keeps
 * the graceful behavior and adds enough breadcrumbs for ops to tell the
 * difference, including whether the failure happened on the dashboard
 * (a nuisance the user can refresh out of) or inside a live meeting
 * (where the user is committed to the call and can't trivially recover).
 *
 * NOTE: this only fires for "Session throws on mount" (path 1 in the
 * fix plan terminology). It does NOT fire when Session mounts cleanly
 * but its WebSocket fails to connect (paths 2/3) — that silent failure
 * is handled by `<ChatboxStuckDetector>` at the Chatbox surface level.
 * See docs/chat-stuck-spinner-fix-plan.md.
 */
function logChatSessionError(error: unknown) {
  const route =
    typeof window !== "undefined" ? window.location.pathname : "<ssr>";
  const isInMeeting = route.startsWith("/video/meeting/");
  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? error.stack : undefined;

  console.error("talkjs.session.init.failed", {
    message,
    stack,
    route,
    isInMeeting,
  });
}

export function ChatSession({ talkjsData, children }: Props) {
  const { key: retryKey } = useChatSessionRetry();

  // Memoized syncUser factory whose function reference flips whenever
  // retryKey bumps. <Session> from @talkjs/react creates Talk.Session
  // inside a useEffect whose dep array includes syncUser; passing a new
  // function reference triggers the effect to destroy the old SDK
  // instance and create a new one IN PLACE — <Session> itself does not
  // remount, and its children (AppLayout / VideoLayout / Chime providers
  // / meeting page / video tracks) stay mounted.
  //
  // Pass syncUser instead of userId — see docs/chat-stuck-spinner-fix-plan.md
  // §5.4. The retryKey dep is load-bearing: it forces this memo to
  // produce a new function reference on each retry, which in turn
  // triggers <Session>'s internal init useEffect to destroy and
  // recreate the Talk.Session WITHOUT unmounting <Session> itself or
  // its children. Do not "simplify" by reverting to
  // userId={talkjsData.user_id} — that breaks the retry mechanism.
  //
  // Deps are intentionally fine-grained:
  //   - talkjsData?.user_id, NOT talkjsData itself: a token rotation
  //     that re-creates the response object (same user_id, new token)
  //     should NOT trigger SDK re-init via this memo. Token changes are
  //     passed to <Session> separately. Including the whole talkjsData
  //     object would spuriously bust the memo on every refetch.
  //   - retryKey is the load-bearing dep that drives the recovery flow.
  const syncUser = useMemo(() => {
    if (!talkjsData?.user_id) return undefined;
    const userId = talkjsData.user_id;
    return () => new Talk.User(userId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talkjsData?.user_id, retryKey]);

  if (!talkjsData) {
    return <>{children}</>;
  }

  if (
    !talkjsData.app_id ||
    !talkjsData.user_id ||
    !talkjsData.token ||
    !syncUser
  ) {
    return <>{children}</>;
  }

  return (
    <ErrorBoundary fallback={<>{children}</>} onError={logChatSessionError}>
      <Session
        appId={talkjsData.app_id}
        syncUser={syncUser}
        token={talkjsData.token}
      >
        {children}
      </Session>
    </ErrorBoundary>
  );
}

export default ChatSession;
